import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, HardDrive, PackageSearch, RefreshCw, Replace, Truck } from 'lucide-react';
import {
  hardwareApi, orderBillingLabel, shortDate as orderDate,
  ORDER_STATUS_CHIP, ORDER_STATUS_LABEL, UNIT_STATUS_CHIP, UNIT_STATUS_LABEL,
  type DeviceOrderRow, type DeviceUnitRow,
} from '../../services/hardwareApi';
import { Card, CardHeader, ConfirmDialog, Empty, Note } from './financeUi';
import {
  money, agreementView, agreementActionBlock, agreementSubmitBlock, agreementActionEffect,
  AGREEMENT_ACTION_LABEL, STRIPE_STEP_PENDING, type AgreementAction,
} from './financeFormat';
import { RubyLoader } from '../RubyLoader';

import { RubyGemMark } from '../RubyGemMark';
/**
 * FINANCE → DEVICES
 *
 * Spec §9: units, order + shipping status, % paid, waive · settle ·
 * replacement requests.
 *
 * This is the TENANT-scoped summary, not a second fulfilment screen. Serial
 * allocation, per-device status and tracking are done once, on Device
 * Configurations → Orders, and every order here links straight to it.
 */

/** Statuses the Hardware total already excludes, while Stripe may still be charging. */
const TERMINAL_AGREEMENTS = new Set(['settled', 'waived']);

interface Props {
  units: DeviceUnitRow[];
  orders: DeviceOrderRow[];
  modelNames: Record<string, string>;
  locationNames: Record<string, string>;
  loading: boolean;
  /** Units and orders are separate reads; each empty state defers to its own. */
  unitsError: string | null;
  ordersError: string | null;
  onReload: () => Promise<void> | void;
  notify: (message: string) => void;
}

const DevicesPanel: React.FC<Props> = ({
  units, orders, modelNames, locationNames, loading, unitsError, ordersError, onReload, notify,
}) => {
  const navigate = useNavigate();
  const [pending, setPending] = useState<{ unit: DeviceUnitRow; action: AgreementAction } | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const financed = useMemo(() => units.filter((u) => !!u.agreement), [units]);
  const unfinanced = useMemo(() => units.filter((u) => !u.agreement), [units]);

  const activeMonthly = useMemo(
    () => financed
      .filter((u) => (u.agreement?.status || 'active') === 'active')
      .reduce((sum, u) => sum + (u.agreement?.monthlyAmountCents ?? 0), 0),
    [financed],
  );
  const outstanding = useMemo(
    () => financed
      .filter((u) => (u.agreement?.status || 'active') === 'active')
      .reduce((sum, u) => sum + agreementView(u.agreement).outstandingCents, 0),
    [financed],
  );

  const submit = async () => {
    if (!pending) return;
    const blocked = agreementSubmitBlock(pending.unit.agreement, reason);
    if (blocked) { notify(blocked); return; }
    setBusy(true);
    try {
      const res = await hardwareApi.agreementAction(pending.unit.unitId, {
        action: pending.action,
        reason: reason.trim(),
      });
      // Report the amount only when the service actually returned one: a
      // confident "$0.00" against a real write-off would be worse than silence.
      const amount = typeof res.audit?.amountCents === 'number' ? ` — ${money(res.audit.amountCents)}` : '';
      notify(
        `${AGREEMENT_ACTION_LABEL[pending.action]} recorded for ` +
        `${modelNames[pending.unit.modelId] || pending.unit.modelId}${amount}. This cannot be undone. ` +
        'The Stripe cancellation or invoice is a separate step and has NOT run.',
      );
      setPending(null);
      setReason('');
      await onReload();
    } catch (err: any) {
      notify(err?.message || 'The agreement action was refused');
    } finally {
      setBusy(false);
    }
  };

  const openFulfilment = () => navigate('/inventory/orders');

  const whereLabel = (u: DeviceUnitRow) =>
    (u.locationId && locationNames[u.locationId]) || u.locationId || 'unassigned';

  return (
    <div className="space-y-4 text-stone-800">
      {unitsError && <Note tone="warn">{unitsError}</Note>}
      {ordersError && ordersError !== unitsError && <Note tone="warn">{ordersError}</Note>}

      {/* Agreements ---------------------------------------------------------- */}
      <Card>
        <CardHeader
          icon={<HardDrive size={15} />}
          title="Devices &amp; agreements"
          hint={financed.length ? `${money(activeMonthly)}/mo · ${money(outstanding)} outstanding` : undefined}
          actions={
            <button
              onClick={onReload}
              disabled={loading}
              className="text-xs text-stone-500 hover:text-stone-900 inline-flex items-center gap-1.5 min-h-[36px] px-2"
            >
              {loading ? <RubyGemMark size={13} drawing color="currentColor" /> : <RefreshCw size={13} />} refresh
            </button>
          }
        />

        {loading && units.length === 0 && !unitsError ? (
          <RubyLoader variant="section" className="py-10" />
        ) : unitsError && units.length === 0 ? (
          // The banner above already says the read failed; the empty state must
          // not sit under it asserting the tenant has no devices, which is a
          // different claim and may well be false.
          <div className="p-4">
            <Note tone="warn">
              Nothing was returned, so whether this tenant has devices is unknown. This is not the same as having
              none — try refresh.
            </Note>
          </div>
        ) : units.length === 0 ? (
          <Empty
            icon={<HardDrive size={34} />}
            title="No devices allocated to this tenant"
            text="Devices appear once an order is placed and serials are allocated on Device Management → Orders."
          />
        ) : (
          <div className="p-4 space-y-3">
            {financed.map((u) => {
              const view = agreementView(u.agreement);
              const status = u.agreement?.status || 'active';
              const blocked = agreementActionBlock(u.agreement);
              return (
                <div key={u.unitId} className="flex items-center gap-3 p-3 rounded-xl border border-stone-200 flex-wrap md:flex-nowrap text-stone-800">
                  <div className="w-11 h-11 rounded-xl bg-stone-900 text-white flex items-center justify-center shrink-0">
                    <HardDrive size={18} />
                  </div>
                  <div className="flex-1 min-w-[12rem]">
                    <div className="font-semibold text-sm text-stone-900">
                      {modelNames[u.modelId] || u.modelId}{' '}
                      <span className="font-mono text-[11px] text-stone-500">{u.serialNumber || 'no serial'}</span>
                    </div>
                    <div className="text-[11px] text-stone-500">
                      {whereLabel(u)} · {u.agreement?.saleMode || 'agreement'}
                      {u.agreement?.termMonths ? ` ${u.agreement.termMonths}mo` : ''} · {view.paidLabel}
                    </div>
                    <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${UNIT_STATUS_CHIP[u.state] || 'bg-stone-200 text-stone-700'}`}>
                        {UNIT_STATUS_LABEL[u.state] || u.state}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
                        status === 'active' ? 'bg-blue-100 text-blue-700'
                          : status === 'defaulted' ? 'bg-red-100 text-red-700'
                            : 'bg-stone-200 text-stone-700'
                      }`}>
                        {status}
                      </span>
                    </div>
                  </div>
                  <div className="w-28 shrink-0">
                    <div className="h-2 rounded-full bg-stone-200 overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: `${view.fraction * 100}%` }} />
                    </div>
                    <div className="text-[10px] text-stone-500 text-center mt-0.5 tabular-nums">{view.percentLabel}</div>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <button
                      onClick={() => { setReason(''); setPending({ unit: u, action: 'waive' }); }}
                      disabled={!!blocked}
                      title={blocked || 'Write off the outstanding balance'}
                      className="px-2.5 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-[11px] font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Waive
                    </button>
                    <button
                      onClick={() => { setReason(''); setPending({ unit: u, action: 'settle' }); }}
                      disabled={!!blocked}
                      title={blocked || 'Invoice the outstanding balance once'}
                      className="px-2.5 py-1.5 rounded-lg border border-stone-300 text-[11px] font-semibold text-stone-700 hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Settle early
                    </button>
                  </div>
                </div>
              );
            })}

            {unfinanced.length > 0 && (
              <div className="rounded-xl border border-stone-200">
                <div className="px-3 py-2 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-wide text-stone-500">
                  {unfinanced.length} device{unfinanced.length === 1 ? '' : 's'} with no finance agreement
                </div>
                <div className="divide-y divide-stone-100">
                  {unfinanced.map((u) => (
                    <div key={u.unitId} className="px-3 py-2 flex items-center justify-between gap-3 text-sm text-stone-700">
                      <span className="truncate">
                        {modelNames[u.modelId] || u.modelId}{' '}
                        <span className="font-mono text-[11px] text-stone-500">{u.serialNumber || 'no serial'}</span>
                        <span className="text-[11px] text-stone-400"> · {whereLabel(u)}</span>
                      </span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide shrink-0 ${UNIT_STATUS_CHIP[u.state] || 'bg-stone-200 text-stone-700'}`}>
                        {UNIT_STATUS_LABEL[u.state] || u.state}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Note>
              <strong>Audit</strong> — every waive and early settlement records who, when, how much and why. Neither
              can be undone from this screen. The trail is on the History panel.
            </Note>
            {financed.some((u) => TERMINAL_AGREEMENTS.has(u.agreement?.status || 'active')) && (
              <Note tone="warn">
                <strong>Closed here, not yet in Stripe.</strong> {STRIPE_STEP_PENDING}
              </Note>
            )}
          </div>
        )}
      </Card>

      {/* Orders --------------------------------------------------------------- */}
      <Card>
        <CardHeader
          icon={<Truck size={15} />}
          title="Orders &amp; shipping"
          hint={orders.length ? `${orders.length} order${orders.length === 1 ? '' : 's'}` : undefined}
          actions={
            <button
              onClick={openFulfilment}
              className="text-xs font-semibold text-stone-700 hover:text-black inline-flex items-center gap-1.5 min-h-[36px] px-2"
            >
              Fulfilment screen <ExternalLink size={12} />
            </button>
          }
        />
        {orders.length === 0 && ordersError ? (
          <div className="p-4">
            <Note tone="warn">Orders could not be read either, so this is not "no orders" — it is nothing returned.</Note>
          </div>
        ) : orders.length === 0 ? (
          <Empty icon={<PackageSearch size={34} />} title="No device orders for this tenant" />
        ) : (
          <div className="divide-y divide-stone-100">
            {orders.map((o) => (
              <div key={o.orderId} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-stone-600">{o.orderId}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${ORDER_STATUS_CHIP[o.status] || 'bg-stone-200 text-stone-700'}`}>
                      {ORDER_STATUS_LABEL[o.status] || o.status}{o.partial ? ' (partial)' : ''}
                    </span>
                  </div>
                  <p className="text-[11px] text-stone-500 mt-0.5">
                    {locationNames[o.locationId] || o.locationId} · placed {orderDate(o.createdAt)} ·{' '}
                    {orderBillingLabel(o.items)}
                    {o.trackingNumber ? ` · ${o.carrier || 'carrier'} ${o.trackingNumber}` : ''}
                  </p>
                </div>
                <button
                  onClick={openFulfilment}
                  className="px-3 py-1.5 rounded-lg border border-stone-300 text-[11px] font-semibold text-stone-700 hover:bg-stone-100 inline-flex items-center gap-1"
                >
                  Open <ExternalLink size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
        {orders.some((o) => o.partial) && (
          <div className="px-4 py-3 border-t border-stone-200">
            <Note>
              An order marked <strong>partial</strong> reads its least-advanced device, so nobody is told a kit is on
              its way while part of it is still backordered.
            </Note>
          </div>
        )}
      </Card>

      {/* Replacements --------------------------------------------------------- */}
      <Card className="border-dashed">
        <CardHeader icon={<Replace size={15} />} title="Replacement requests" />
        <div className="p-4">
          <p className="text-xs text-stone-600 leading-relaxed">
            Not built yet. §7.4 has the shape — the tenant raises a request against a unit, super admin approves, a new
            unit is allocated and the old one goes to RMA, and the <strong>agreement stays with the tenant, not the
            device</strong>, so a warranty swap never restarts or duplicates someone's financing. There is no endpoint
            behind it today, so nothing is listed here rather than an empty list that would read as "no requests".
          </p>
        </div>
      </Card>

      {/* Waive / settle -------------------------------------------------------- */}
      <ConfirmDialog
        open={!!pending}
        title={pending ? `${AGREEMENT_ACTION_LABEL[pending.action]} this agreement?` : ''}
        tone="danger"
        confirmLabel={pending ? AGREEMENT_ACTION_LABEL[pending.action] : ''}
        busy={busy}
        blocked={pending ? agreementSubmitBlock(pending.unit.agreement, reason) : null}
        onConfirm={submit}
        onClose={() => { setPending(null); setReason(''); }}
      >
        {pending && (
          <>
            <p className="text-stone-700">
              <strong>{modelNames[pending.unit.modelId] || pending.unit.modelId}</strong>{' '}
              <span className="font-mono text-xs text-stone-500">{pending.unit.serialNumber || 'no serial'}</span>
              {' — '}{agreementView(pending.unit.agreement).paidLabel}.
            </p>
            <p className="text-stone-700">{agreementActionEffect(pending.action, pending.unit.agreement)}</p>
            <Note tone="warn">{STRIPE_STEP_PENDING}</Note>
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900">
              This is <strong>not reversible</strong> from this screen. Your name, the time, the amount and the reason
              below are written to the audit trail in the same transaction as the change itself.
            </div>
            <label className="block">
              <span className="block text-xs font-semibold text-stone-700 mb-1">Reason (required)</span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="Why this agreement is being written off or settled…"
                className="w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-stone-800 placeholder-stone-400 resize-none"
              />
            </label>
          </>
        )}
      </ConfirmDialog>
    </div>
  );
};

export default DevicesPanel;
